import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { MessageSquare, RotateCcw, Save } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { ReceiptEmailCard } from "@/components/ReceiptEmailCard";

const KINDS = [
  { key: "confirmation", title: "Booking Confirmation", hint: "Sent right after a client pays (or you mark an admin booking paid with confirmation on)." },
  { key: "reminder", title: "24-Hour Reminder", hint: "Sent about 24 hours before the session." },
  { key: "help", title: "HELP Reply", hint: "Sent when a client texts HELP or INFO." },
  { key: "cancellation", title: "Booking Cancelled", hint: "Sent when you cancel a booking with “Notify customer” on. {reason} adds your note, if you wrote one." },
  { key: "reschedule", title: "Booking Rescheduled", hint: "Sent when you reschedule a booking with “Notify client” on. {date}/{time} are the new time; {reason} adds your note." },
];
const TAGS = ["first_name", "name", "service", "date", "time", "end_time", "hours", "address", "studio_phone", "reason", "door_code"];
const SAMPLE = { first_name: "Jordan", name: "Jordan Miles", service: "Recording & Podcast Studio", date: "Saturday, October 24, 2026",
  time: "2:00 PM", end_time: "4:00 PM", hours: "2", address: "3020 High Ridge Rd, Suite 600A, Boynton Beach, FL", studio_phone: "561-503-7743", reason: " Note: Studio maintenance — we'll call you to reschedule.", door_code: " Your door code: 4829173 (works from 15 min before your session until it ends)." };

const render = (tpl) => {
  const out = (tpl || "").replace(/\{(\w+)\}/g, (m, k) => SAMPLE[k] ?? m).trim();
  return /\bstop\b/i.test(out) ? out : `${out} Reply STOP to opt out.`;
};

const TemplateCard = ({ kind, value, def, onChange }) => {
  const ref = useRef(null);
  const insert = (tag) => {
    const el = ref.current;
    const pos = el ? el.selectionStart : value.length;
    const next = `${value.slice(0, pos)}{${tag}}${value.slice(el ? el.selectionEnd : pos)}`;
    onChange(next);
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(pos + tag.length + 2, pos + tag.length + 2); });
  };
  const preview = render(value);
  const segs = Math.max(1, Math.ceil(preview.length / 153));
  return (
    <div className="bg-[#111] border border-white/10 rounded-2xl p-5" data-testid={`sms-card-${kind.key}`}>
      <div className="flex items-start justify-between gap-3 mb-1">
        <h2 className="font-display text-lg text-white">{kind.title}</h2>
        <button type="button" onClick={() => onChange(def)} data-testid={`sms-reset-${kind.key}`} className="text-xs text-white/50 hover:text-[#D4AF37] flex items-center gap-1"><RotateCcw size={12} /> Reset to default</button>
      </div>
      <p className="text-white/40 text-xs mb-3">{kind.hint}</p>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {TAGS.map((t) => (
          <button key={t} type="button" onClick={() => insert(t)} data-testid={`sms-tag-${kind.key}-${t}`}
            className="text-[10px] font-mono-label px-2 py-1 rounded-full border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10">{`{${t}}`}</button>
        ))}
      </div>
      <Textarea ref={ref} rows={4} maxLength={600} value={value} onChange={(e) => onChange(e.target.value)}
        className="bg-[#0a0a0a] border-white/15 text-sm" data-testid={`sms-input-${kind.key}`} />
      <div className="mt-3 rounded-xl bg-[#0a0a0a] border border-white/5 p-3">
        <p className="text-[10px] uppercase font-mono-label text-white/40 mb-1.5 flex items-center gap-1"><MessageSquare size={11} /> Preview</p>
        <p className="text-white/80 text-sm whitespace-pre-line" data-testid={`sms-preview-${kind.key}`}>{preview}</p>
        <p className={`text-[11px] mt-2 ${preview.length > 320 ? "text-orange-400" : "text-white/35"}`} data-testid={`sms-count-${kind.key}`}>
          {preview.length} characters · about {segs} text segment{segs > 1 ? "s" : ""}{!/\bstop\b/i.test(value) ? " · “Reply STOP to opt out.” added automatically" : ""}
        </p>
      </div>
    </div>
  );
};

export default function TextMessages() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["sms-templates"], queryFn: () => api.get("/admin/sms-templates").then((r) => r.data) });
  const [vals, setVals] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (data && !vals) setVals(data.templates); }, [data, vals]);
  if (!vals) return null;
  const dirty = KINDS.some((k) => vals[k.key] !== data.templates[k.key]);
  const save = async () => {
    setSaving(true);
    try { const r = await api.put("/admin/sms-templates", vals); setVals(r.data.templates); qc.setQueryData(["sms-templates"], { ...data, templates: r.data.templates }); toast.success("Text messages saved"); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <div data-testid="admin-text-messages" className="max-w-3xl">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl text-white mb-1">Text Messages</h1>
          <p className="text-white/50 text-sm">Edit the texts clients receive. Only clients who ticked the SMS box get texts.</p>
        </div>
        <button onClick={save} disabled={!dirty || saving} data-testid="sms-save-btn"
          className="btn-gold rounded-full px-6 py-2.5 text-xs font-mono-label uppercase flex items-center gap-2 disabled:opacity-40"><Save size={14} /> {saving ? "Saving…" : "Save changes"}</button>
      </div>
      <div className="space-y-5">
        {KINDS.map((k) => <TemplateCard key={k.key} kind={k} value={vals[k.key]} def={data.defaults[k.key]} onChange={(v) => setVals({ ...vals, [k.key]: v })} />)}
      </div>
      <div className="mt-10"><ReceiptEmailCard /></div>
    </div>
  );
}
